import React from "react";
import { Poptip, Typography, VerticalTab } from "@optimizely/axiom";
import { NavLink, useLocation } from "react-router-dom";
import classNames from "classnames";
import { isChildPath } from "../../../lib/navigation-helpers";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag/useFeatureFlag";
import { useOpalBranding } from "../../../hooks/useOpalBranding/useOpalBranding";
import { Flags } from "../../../feature-flags/flags";
import { SETTINGS_ROUTE } from "../../../constants/route-constants";

export const NavigationBarSettingsSubMenu = () => {
    const location = useLocation();
    const pathname = location.pathname;
    const { opalName, renameOpalCopy } = useOpalBranding();
    const { enabled: genAIEnabled } = useFeatureFlag(Flags.ENABLE_GEN_AI);
    const { enabled: billingNotificationsEnabled } = useFeatureFlag(Flags.ENABLE_BILLING_NOTIFICATION_SETTINGS);
    const { enabled: developerSettingsEnabled } = useFeatureFlag(Flags.ENABLE_DEVELOPER_SETTINGS);
    const { enabled: showNavigationTooltipFeatureEnabled } = useFeatureFlag(Flags.ENABLE_NAVIGATION_TOOLTIPS);

    if (!isChildPath({ pathname: location.pathname, path: "/settings/" })) {
        return null;
    }

    // Single source of truth for the Organization sub-route: resolve its path, title, and tooltip
    // from SETTINGS_ROUTE so changes in the constant can't silently diverge from the nav link, and
    // there's nothing to non-null assert.
    const organizationSubRoute = SETTINGS_ROUTE.subRoutes?.find((route) => route.name === "organization");
    const organizationPath = organizationSubRoute?.path ?? "";
    const organizationTitle = organizationSubRoute?.title ?? "Organization";
    const organizationTooltip =
        SETTINGS_ROUTE.menuItem?.subItems?.find((item) => item.href === organizationPath)?.tooltip ?? "";

    const menuItemClasses = (route: string) => {
        return classNames({
            "oui-manager-side-nav__custom-link": true
        });
    };

    const NavItemTooltip = ({ children, tooltip }: { children: React.ReactNode; tooltip: string }) => {
        if (!showNavigationTooltipFeatureEnabled) {
            return <>{children}</>;
        }
        return (
            <Poptip isInline position="top" trigger="mouseenter" content={tooltip} className="width--full">
                {children}
            </Poptip>
        );
    };

    return (
        <VerticalTab>
            <VerticalTab.Header hasBackLink={false}>
                <Typography type="header4" tag="h4">
                    Settings
                </Typography>
            </VerticalTab.Header>
            <VerticalTab.NavList>
                <VerticalTab.NavItem
                    isActive={isChildPath({ pathname, path: "/settings/authentication" })}
                    leftContent={
                        <NavItemTooltip tooltip={SETTINGS_ROUTE.menuItem!.subItems![0].tooltip}>
                            <NavLink
                                className={menuItemClasses("/settings/authentication")}
                                to="/settings/authentication/sso"
                            >
                                Authentication
                            </NavLink>
                        </NavItemTooltip>
                    }
                />
                {billingNotificationsEnabled && (
                    <VerticalTab.NavItem
                        isActive={isChildPath({ pathname, path: "/settings/notifications" })}
                        leftContent={
                            <NavItemTooltip
                                tooltip={
                                    SETTINGS_ROUTE.menuItem!.subItems!.find(
                                        (item) => item.href === "/settings/notifications"
                                    )?.tooltip || ""
                                }
                            >
                                <NavLink
                                    className={menuItemClasses("/settings/notifications")}
                                    to="/settings/notifications"
                                >
                                    Notifications
                                </NavLink>
                            </NavItemTooltip>
                        }
                    />
                )}
                {genAIEnabled && (
                    <VerticalTab.NavItem
                        isActive={isChildPath({ pathname, path: "/settings/genai" })}
                        leftContent={
                            <NavItemTooltip
                                tooltip={renameOpalCopy(
                                    SETTINGS_ROUTE.menuItem!.subItems!.find((item) => item.href === "/settings/genai")
                                        ?.tooltip || ""
                                )}
                            >
                                <NavLink className={menuItemClasses("/settings/genai")} to="/settings/genai">
                                    {opalName}
                                </NavLink>
                            </NavItemTooltip>
                        }
                    />
                )}
                {developerSettingsEnabled && organizationPath && (
                    <VerticalTab.NavItem
                        isActive={isChildPath({ pathname, path: organizationPath })}
                        leftContent={
                            <NavItemTooltip tooltip={organizationTooltip}>
                                <NavLink className={menuItemClasses(organizationPath)} to={organizationPath}>
                                    {organizationTitle}
                                </NavLink>
                            </NavItemTooltip>
                        }
                    />
                )}
            </VerticalTab.NavList>
        </VerticalTab>
    );
};

NavigationBarSettingsSubMenu.displayName = "NavigationBarSettingsSubMenu";
